/**
 * Bundle 2a.1b — Print-ready signal coordinator.
 *
 * The Puppeteer worker pool (Bundle 2a.1a) waits for
 * `body[data-print-ready="true"]` before capturing a PDF. This module
 * coordinates the async sources that must be ready before the signal
 * fires:
 *
 *   - i18next initialisation (i18n bundle ready)
 *   - document.fonts.ready (web fonts loaded)
 *   - patient report data fetch
 *   - per-image onload (chart images, QR codes, logos)
 *
 * Pattern: async sources register a named pending task on mount;
 * each calls `completePrintTask(name)` when it's done. When the
 * pending set is empty AND at least one task has completed, the
 * signal fires on `<body>` after a double `requestAnimationFrame`
 * (ensures layout + paint settle before capture).
 *
 * The coordinator is a no-op outside print mode — non-print SPA
 * paints are unaffected. `isPrintMode()` reads `?print=1` from the
 * current URL.
 */

const pendingTasks = new Set<string>();
const completedTasks = new Set<string>();
let signalFired = false;

export function isPrintMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('print') === '1';
  } catch {
    return false;
  }
}

/**
 * Register a task that must complete before the print-ready signal
 * fires. No-op outside print mode. If the signal has already fired
 * (a late registration after capture started), logs a warning.
 *
 * Names should be stable + diagnostic — they appear in the
 * `getPrintReadyState()` debug view + the console.log on signal fire.
 * Conventions:
 *   - dash-case
 *   - prefixed by source class: `data-`, `img-`, etc.
 *   - per-instance suffix when there are multiples: `img-clinic-logo`
 */
export function registerPrintTask(name: string): void {
  if (!isPrintMode()) return;
  if (signalFired) {
    // eslint-disable-next-line no-console
    console.warn(`[printReady] late task registration: ${name} (signal already fired)`);
    return;
  }
  pendingTasks.add(name);
}

/**
 * Mark a task complete. No-op outside print mode. Calling with a name
 * that was never registered is harmless — `pendingTasks.delete` of a
 * non-existent key returns false silently. We still record to
 * `completedTasks` so the signal-fire diagnostic shows it.
 */
export function completePrintTask(name: string): void {
  if (!isPrintMode()) return;
  pendingTasks.delete(name);
  completedTasks.add(name);
  maybeFireSignal();
}

function maybeFireSignal(): void {
  if (signalFired) return;
  if (pendingTasks.size > 0) return;
  if (completedTasks.size === 0) return; // never fire on empty registry
  // Double-RAF: first frame for layout, second frame for paint to
  // settle. Without this, Puppeteer can capture before the just-
  // completed task's DOM is rendered.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (signalFired) return; // racy concurrent firings
      document.body.setAttribute('data-print-ready', 'true');
      signalFired = true;
      // eslint-disable-next-line no-console
      console.log('[printReady] signal fired. Completed tasks:', [...completedTasks]);
    });
  });
}

/**
 * Diagnostic getter. Exposed for tests + Puppeteer-side debugging
 * (the page can `evaluate(() => window.__printReadyState)` if the
 * signal fails to fire).
 */
export function getPrintReadyState(): {
  pending: string[];
  completed: string[];
  fired: boolean;
} {
  return {
    pending: [...pendingTasks],
    completed: [...completedTasks],
    fired: signalFired,
  };
}

/**
 * Reset internal state. Test-only — re-imports during HMR also call
 * this implicitly because module-level state is shared.
 *
 * @internal
 */
export function _resetPrintReadyForTests(): void {
  pendingTasks.clear();
  completedTasks.clear();
  signalFired = false;
}

// Expose on window for Puppeteer debugging in dev. Cheap — only fires
// in print mode + only sets a single property.
if (typeof window !== 'undefined' && isPrintMode()) {
  (window as unknown as { __printReadyState: typeof getPrintReadyState }).__printReadyState =
    getPrintReadyState;
}
